import { createRouter, createWebHistory } from 'vue-router'
import { supabase } from '../utils/supabaseClient'
import { subscriptionService } from '../utils/paypalService'

// Create a meta field for routes that require authentication
const requireAuth = async (to, from, next) => {
  try {
    const { data, error } = await supabase.auth.getUser()
    if (error || !data.user) {
      console.error('Authentication check error:', error)
      next('/login')
    } else {
      next()
    }
  } catch (error) {
    console.error('Authentication error:', error)
    next('/login')
  }
}

// Create a route guard that checks subscription limits for quote creation
const requireQuoteCreationAccess = async (to, from, next) => {
  try {
    // First check authentication
    const { data, error } = await supabase.auth.getUser()
    if (error || !data.user) {
      console.error('Authentication check error:', error)
      next('/login')
      return
    }

    // Check if user can create quotes with proper error handling
    try {
      const canCreate = await subscriptionService.canCreateQuote(data.user.id)
      
      if (!canCreate) {
        // Get subscription stats to show in upgrade modal
        const stats = await subscriptionService.getSubscriptionStats(data.user.id)
        
        // Redirect to dashboard with upgrade query parameter and stats
        next({ 
          path: '/dashboard', 
          query: { 
            showUpgrade: 'true',
            reason: 'quota_exceeded',
            used: stats.quotesUsed,
            limit: stats.quotesLimit
          } 
        })
      } else {
        next()
      }
    } catch (subscriptionError) {
      console.error('Subscription check error:', subscriptionError)
      // On subscription check error, redirect to dashboard with error message
      next({ 
        path: '/dashboard', 
        query: { 
          error: 'subscription_check_failed',
          message: 'Unable to verify subscription status. Please try again.'
        } 
      })
    }
  } catch (error) {
    console.error('Route guard error:', error)
    // On general error, redirect to login
    next('/login')
  }
}

// Create a route guard that checks subscription for analytics access
const requireAnalyticsAccess = async (to, from, next) => {
  try {
    // First check authentication
    const { data, error } = await supabase.auth.getUser()
    if (error || !data.user) {
      console.error('Authentication check error:', error)
      next('/login')
      return
    }

    // Check if user has premium subscription for analytics access
    try {
      const stats = await subscriptionService.getSubscriptionStats(data.user.id)
      
      // Only premium users can access analytics
      if (stats.subscriptionType !== 'premium') {
        // Redirect to dashboard with upgrade query parameter
        next({ 
          path: '/dashboard', 
          query: { 
            showUpgrade: 'true',
            reason: 'analytics_access',
            feature: 'analytics'
          } 
        })
      } else {
        next()
      }
    } catch (subscriptionError) {
      console.error('Subscription check error:', subscriptionError)
      // On subscription check error, redirect to dashboard with error message
      next({ 
        path: '/dashboard', 
        query: { 
          error: 'subscription_check_failed',
          message: 'Unable to verify subscription status. Please try again.'
        } 
      })
    }
  } catch (error) {
    console.error('Route guard error:', error)
    // On general error, redirect to login
    next('/login')
  }
}

// Create a meta field for admin-only routes
// const requireAdmin = async (to, from, next) => {
//   const { data: userData } = await supabase.auth.getUser()
//   if (!userData.user) {
//     next('/login')
//     return
//   }
  
//   // Check if user has admin role
//   // In a real app, you would check a specific admin flag or role
//   // For demo, we'll just allow any authenticated user to access admin features
//   next()
// }

// Create a meta field for guests only routes (login, signup)
const requireGuest = async (to, from, next) => {
  try {
    const { data } = await supabase.auth.getUser()
    if (data.user) {
      next('/quotes')
    } else {
      next()
    }
  } catch (error) {
    console.error('Auth check error:', error)
    // If auth check fails, allow access to guest pages
    next()
  }
}

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    beforeEnter: requireGuest
  },
  {
    // Special route that forces a page reload for the login page
    path: '/login-refresh',
    // eslint-disable-next-line no-unused-vars
    beforeEnter: (to, from, next) => {
      window.location.href = '/login'
    }
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('../views/SignupView.vue'),
    beforeEnter: requireGuest
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    beforeEnter: requireAuth
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
    beforeEnter: requireAuth
  },
  // Quote related routes
  {
    path: '/quotes',
    name: 'quotes',
    component: () => import('../views/QuotesListView.vue'),
    beforeEnter: requireAuth
  },
  {
    path: '/quotes/create',
    name: 'quote-create',
    component: () => import('../views/QuoteGeneratorView.vue'),
    beforeEnter: requireQuoteCreationAccess
  },
  {
    path: '/quotes/:id',
    name: 'quote-view',
    component: () => import('../views/QuoteGeneratorView.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('../views/AnalyticsDashboardView.vue'),
    beforeEnter: requireAnalyticsAccess
  },
  {
    path: '/products',
    name: 'my-products',
    component: () => import('../views/MyProductsView.vue'),
    beforeEnter: requireAuth
  },
  {
    path: '/quote/public/:token',
    name: 'public-quote',
    component: () => import('../views/PublicQuoteView.vue')
  },
  {
    // Catch all route - redirect to login
    path: '/:pathMatch(.*)*',
    redirect: '/login'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

export default router